import { PayStatement } from "@wingspanhq/wingspan-api-sdk";
import uniq from "lodash/uniq";

import {
  formatPayPeriodRange,
  parsePayrollAmount
} from "../../PayrollRuns/utils";
import { PaystubRecentPay } from "../types";

const EMPTY_VALUE = "—";

/**
 * Adapt a v3 `PayStatement` into the "Recent pay" card view model (payee
 * perspective).
 *
 * `taxes` is the statement's total worker tax withheld; `benefits` is the
 * residual of every other deduction (gross − net − taxes) so the card's
 * breakdown reconciles to net pay — the v3 pay statement does not model pre-tax
 * benefit deductions as their own line (see `selectorPaystubDetails`).
 *
 * TODO(v3): the payout-method fields fall back — resolving them needs
 * PayoutSettings + ExternalBankAccount, gated by a payer→payee bank-data grant
 * (PLA-3198).
 */
export const selectorPaystubRecentPay = (
  payStatement: PayStatement
): PaystubRecentPay => {
  const { amountDetails } = payStatement;
  const grossAmount = parsePayrollAmount(amountDetails?.grossPay);
  const netAmount = parsePayrollAmount(amountDetails?.netPay);
  const taxes = parsePayrollAmount(amountDetails?.taxWithheld);

  const workLogIds = uniq(
    (payStatement.earnings ?? [])
      .map(earning => earning.workLogId)
      .filter((id): id is string => !!id)
  );

  return {
    paystubId: payStatement.id ?? "",
    payPeriodLabel: formatPayPeriodRange(
      payStatement.payPeriodStartDate,
      payStatement.payPeriodEndDate
    ),
    netAmount,
    grossAmount,
    taxes,
    benefits: Math.max(grossAmount - netAmount - taxes, 0),
    payDate: payStatement.payDate,
    workLogsCount: workLogIds.length,
    payoutMethodLabel: EMPTY_VALUE,
    payoutAccountLabel: "",
    payoutPercentage: 0
  };
};
